import { DataSource, FiltrosModel } from '@/datasources/consulte-grid';

/**
 * DataSource em MEMÓRIA para o ConsulteGrid.
 *
 * O `DataSource` padrão nasceu para endpoint paginado: cada `load()` é uma ida ao servidor. Boa
 * parte das listas do sistema, porém, já tem todos os registros na tela — séries de um ensino,
 * itens de um cardápio, escolas de um ensino, linhas de um modal de seleção. Elas não têm (nem
 * precisam de) endpoint paginado, e era por isso que continuavam como `<table>` na mão.
 *
 * Esta classe fecha essa lacuna: recebe a lista inteira, e faz filtro, ordenação e paginação no
 * cliente. Assim toda listagem do sistema usa o MESMO grid, com os mesmos filtros, seleção de
 * colunas, exportação e visual — sem precisar de backend novo e sem tocar no componente.
 *
 * Uso:
 * ```ts
 * dataSource = new DataSourceLocal<MinhaLinha>(linhas);        // ou new DataSourceLocal([])
 * this.dataSource.definirItens(await servico.listar());        // recarrega mantendo filtros
 * ```
 */
export class DataSourceLocal<T> extends DataSource<T> {
  /** A lista completa, antes de filtro e paginação. */
  private itens: T[] = [];

  constructor(itens: T[] = [], rowsPerPage = 10) {
    super(0, rowsPerPage, 0, itens.length, []);

    this.itens = itens ?? [];
    this.aplicar();
  }

  /**
   * Troca a lista completa. Mantém os filtros aplicados e volta para a primeira página — é o
   * que se espera depois de salvar ou excluir um registro.
   */
  definirItens(itens: T[]) {
    this.itens = itens ?? [];
    this.pageNumber = 0;
    this.aplicar();
  }

  /** A lista completa, sem filtro nem paginação. */
  obterItens(): T[] {
    return this.itens;
  }

  /**
   * O que o filtro do cabeçalho deixou passar, sem o recorte da página. É o que uma ação em
   * lote ("marcar todos") deve alcançar: o usuário espera atingir o que ele filtrou, não só a
   * página em que está.
   */
  obterFiltrados(): T[] {
    return this.ordenar(this.filtrar(this.itens));
  }

  /**
   * O grid chama `load()` a cada navegação, filtro ou ordenação. Aqui não há requisição: só
   * refazemos o recorte em memória.
   */
  async load(): Promise<any> {
    this.aplicar();

    return super.load();
  }

  private aplicar() {
    const filtrados = this.ordenar(this.filtrar(this.itens));

    this.totalRowCount = filtrados.length;
    this.calculateNumberOfPages();

    // Excluir a última linha de uma página pode deixar a página corrente fora do intervalo.
    if (this.pageNumber > 0 && this.pageNumber >= this.numberOfPages) {
      this.pageNumber = Math.max(0, this.numberOfPages - 1);
    }

    const inicio = this.pageNumber * this.rowsPerPage;

    this.rows = filtrados.slice(inicio, inicio + this.rowsPerPage);
  }

  /**
   * Aplica os filtros do cabeçalho do grid. Suporta as expressões que o
   * `ConsulteGridFilters` emite: contains, notcontains, equals, notequals, startswith,
   * endswith, greaterthan, lessthan, empty e notempty.
   */
  private filtrar(itens: T[]): T[] {
    if (!this.filters?.length) {
      return itens;
    }

    return itens.filter((item: T) =>
      this.filters.every((filtro: FiltrosModel) => {
        if (!filtro.filters?.length) {
          return true;
        }

        const valorDaLinha = (item as any)[filtro.field];

        const resultados = filtro.filters.map((condicao) =>
          this.compara(valorDaLinha, condicao.value, condicao.condition),
        );

        // 'or' entre condições do mesmo campo é o padrão de seleção múltipla do filtro.
        return filtro.conditionBetweenFilters === 'or'
          ? resultados.some(Boolean)
          : resultados.every(Boolean);
      }),
    );
  }

  private compara(valorDaLinha: any, valorFiltro: any, condicao: string): boolean {
    const texto = this.normalizar(valorDaLinha);
    const procurado = this.normalizar(valorFiltro);

    switch ((condicao ?? 'contains').toLowerCase()) {
      case 'equals':
      case 'equal':
        return texto === procurado;

      case 'notequals':
      case 'notequal':
        return texto !== procurado;

      case 'startswith':
        return texto.startsWith(procurado);

      case 'endswith':
        return texto.endsWith(procurado);

      case 'notcontains':
        return !texto.includes(procurado);

      case 'empty':
        return texto.length === 0;

      case 'notempty':
        return texto.length > 0;

      case 'greaterthan':
        return Number(valorDaLinha) > Number(valorFiltro);

      case 'lessthan':
        return Number(valorDaLinha) < Number(valorFiltro);

      default:
        return texto.includes(procurado);
    }
  }

  /** Comparação sem acento e sem caixa — é como o usuário espera filtrar nome próprio. */
  private normalizar(valor: any): string {
    if (valor === null || valor === undefined) {
      return '';
    }

    return String(valor)
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();
  }

  /**
   * Ordena pelo que o cabeçalho do grid pediu.
   *
   * O ConsulteGrid manda a ordenação como uma expressão de SQL — `"nome ASC, matricula DESC"` —,
   * porque os datasources de servidor a repassam para a API. Aqui ela é interpretada: cada campo
   * vira um critério, na ordem em que o usuário clicou. Uma tela que já venha ordenada e sem
   * nenhum clique (`sortDataField` vazio) mantém a ordem em que os itens chegaram.
   */
  private ordenar(itens: T[]): T[] {
    const criterios = this.interpretarOrdenacao();

    if (!criterios.length) {
      return itens;
    }

    return [...itens].sort((a: any, b: any) => {
      for (const criterio of criterios) {
        const comparacao = this.comparar(a?.[criterio.campo], b?.[criterio.campo]);

        if (comparacao !== 0) {
          return criterio.descendente ? -comparacao : comparacao;
        }
      }

      return 0;
    });
  }

  private interpretarOrdenacao(): Array<{ campo: string; descendente: boolean }> {
    const expressao = (this.sortDataField ?? '').trim();

    if (!expressao) {
      return [];
    }

    return expressao
      .split(',')
      .map((parte) => parte.trim())
      .filter((parte) => parte.length > 0)
      .map((parte) => {
        const [campo, direcao] = parte.split(/\s+/);

        return {
          campo,
          // Sem direção explícita vale a do datasource, e o padrão dela é crescente.
          descendente: (direcao ?? this.sortOrder ?? 'asc').toLowerCase() === 'desc',
        };
      });
  }

  private comparar(valorA: any, valorB: any): number {
    if (typeof valorA === 'number' && typeof valorB === 'number') {
      return valorA - valorB;
    }

    return this.normalizar(valorA).localeCompare(this.normalizar(valorB));
  }
}
